import React, { useRef, useEffect, useCallback, useState } from "react";
import { View } from "react-native";
import { Loading, StyleFunction } from "@venuepos/react-common";
import { Button, Text, Headline, useThemedStyle } from "@venuepos/react-common";
import { useTranslation } from "locales";
import type {
    ConflictError,
    ValidationErrorData,
} from "lib/src/validation/types";
import { ValidationErrorsList } from "./validation-errors-list";
import { useAccessToken } from "../../hooks/use-access-token";
import { ConflictErrorsList } from "./conflict-errors-list";

export function ImportProductsModal({
    onClose,
}: {
    onClose: (val: any) => any;
}) {
    const accessToken = useAccessToken();
    const [t] = useTranslation();
    const styles = useThemedStyle(styleFunc);

    const [successful, setSuccessful] = useState<boolean>(false);
    const [importLoading, setImportLoading] = useState<boolean>(false);
    const [importState, setImportState] = useState<string | undefined>();

    const [validationErrors, setValidationErrors] = useState<
        { errors: ValidationErrorData[]; rowIndex: number }[]
    >([]);
    const [conflictErrors, setConflictErrors] = useState<ConflictError[]>([]);
    const inputRef = useRef<HTMLInputElement>(null);

    const handleFileChange = useCallback(async () => {
        const fileName = inputRef.current?.files && inputRef.current!.files[0];
        if (!fileName || !accessToken) {
            return;
        }

        const formData = new FormData();
        formData.append("product-import", fileName);

        // When a new file is uploaded, reset the various state variables
        setImportLoading(true);
        setSuccessful(false);
        setImportState(undefined);
        setValidationErrors([]);
        setConflictErrors([]);

        await fetch("/api/import/product", {
            method: "POST",
            headers: {
                authorization: `Bearer ${accessToken}`,
            },
            body: formData,
        }).then(async response => {
            const jsonResponse = await response.json();

            setImportLoading(false);
            if (jsonResponse.error) {
                setImportState(t(jsonResponse.error));
                if (jsonResponse.validationErrors) {
                    setValidationErrors(jsonResponse.validationErrors);
                } else if (jsonResponse.rowErrors.statusCode === 400) {
                    setValidationErrors(jsonResponse.rowErrors.errors);
                } else if (jsonResponse.rowErrors.statusCode === 409) {
                    setConflictErrors(jsonResponse.rowErrors.errors);
                }

                return;
            }

            setImportState(
                t("import.successful", {
                    itemsAdded: jsonResponse.productsAdded,
                    itemsUpdated: jsonResponse.productsUpdated,
                    itemGroupsAdded: jsonResponse.productGroupsAdded,
                    itemsHandled: jsonResponse.productsHandled,
                })
            );
            setSuccessful(true);
        });
    }, [accessToken, t]);

    useEffect(() => {
        if (inputRef.current === null) {
            return;
        }
        const input = inputRef.current;
        input.click();
        input.addEventListener("change", handleFileChange);
        return () => {
            input.removeEventListener("change", handleFileChange);
        };
    }, [inputRef, handleFileChange]);

    return (
        <View style={styles.container}>
            <Headline>{t("backoffice.products.import", "Import")}</Headline>

            {!successful && (
                <input
                    name="file"
                    ref={inputRef}
                    type="file"
                    style={styles.fileInput as any}
                />
            )}

            {!importLoading ? undefined : (
                <Loading
                    message={t(
                        "backoffice.products.importing",
                        "Importing products..."
                    )}
                />
            )}

            {importState && (
                <Text style={!successful ? styles.error : undefined}>
                    {importState}
                </Text>
            )}

            <ValidationErrorsList errors={validationErrors} />
            <ConflictErrorsList errors={conflictErrors} />

            <Button
                style={styles.closeButton}
                onPress={() => {
                    onClose(null);
                }}
            >
                {t("common.close", "Close")}
            </Button>
        </View>
    );
}

const styleFunc: StyleFunction = theme => ({
    container: {
        backgroundColor: theme.colors.white,
        padding: 20,
        alignSelf: "center",
        width: "100%",
        maxWidth: 500,
    },
    fileInput: {
        marginBottom: 20,
    },
    closeButton: {
        marginTop: 20,
    },
    error: {
        color: theme.colors.error,
    },
});
